import { useState } from 'react';
import { FormTitle } from '../../components/FormTitle';
import { limsRoundText, formatSigNotation } from '../analysis/limsRoundFn';
import '../backlog/backlog.css';
import '../modules/SettingsDetailPage.css';   // shared .sd-actionbar / .sd-abtn action bars

// ─────────────────────────────────────────────────────────────────────────────
// Settings ▸ Test & Method ▸ Round Off Test  (port of legacy XAF "Round Off Test")
//
// An interactive test harness for the lab's rounding rule — NOT a CRUD form (it
// persists nothing). You set the rounding parameters (Sig Figures · Cut Off ·
// Decimals · Default Result · Rpt Limit · Sci Notation Decimals), enter numbers,
// and "Show Results!" displays each number's reported result + scientific notation.
//
// The maths REUSES the already-verified engine (Rule 8 — never reinvent):
//   • Display Results          → limsRoundText(value, sigFig, cutOff, decimals, rptLimit, defaultResult)
//     the SAME 3-rule cascade as the server-side E_RoundOff / LimsRounding.Apply
//     (value < RptLimit → DefaultResult; RptLimit ≤ v < CutOff → decimals; else sig figs),
//     byte-parity-tested against the legacy E_RoundOff goldens.
//   • Display Scientific Notation → formatSigNotation(value, sciNotationDecimals)
//     OG's own FormatSF / SGFNT scientific-notation formatter (verbatim port).
//
// ⚠️ ASSUMPTION (flag for side-by-side check vs legacy): "Sci Notation Decimals"
// is fed to OG's sci-notation formatter as its significant-figure count — OG has a
// single sci-notation formatter (FormatSF) and this field drives that column. If a
// side-by-side shows it should be mantissa-decimals instead, change the one call below.
// ─────────────────────────────────────────────────────────────────────────────

const ROWS = 4;
const isNumeric = (s: string) => /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s.trim());

export default function RoundOffTestPage() {
  // Setup parameters (kept as strings so a blank means "not configured", like the engine expects).
  const [sigFigures, setSigFigures] = useState('');
  const [cutOff, setCutOff] = useState('');
  const [decimals, setDecimals] = useState('');
  const [defaultResult, setDefaultResult] = useState('');
  const [rptLimit, setRptLimit] = useState('');
  const [sciDecimals, setSciDecimals] = useState('');

  const [inputs, setInputs] = useState<string[]>(Array(ROWS).fill(''));
  const [results, setResults] = useState<string[]>(Array(ROWS).fill(''));
  const [sci, setSci] = useState<string[]>(Array(ROWS).fill(''));

  const setInput = (i: number, v: string) => setInputs((a) => a.map((x, j) => (j === i ? v : x)));

  const showResults = () => {
    const sf = Math.trunc(Number(sciDecimals));
    const sfValid = sciDecimals.trim() !== '' && Number.isFinite(sf) && sf > 0;
    setResults(
      inputs.map((v) =>
        v.trim() === '' ? '' : String(limsRoundText(v, sigFigures, cutOff, decimals, rptLimit, defaultResult))),
    );
    setSci(
      inputs.map((v) => {
        const s = v.trim();
        if (s === '' || !isNumeric(s) || !sfValid) return '';
        return formatSigNotation(Number(s), sf);
      }),
    );
  };

  const clearAll = () => {
    setInputs(Array(ROWS).fill(''));
    setResults(Array(ROWS).fill(''));
    setSci(Array(ROWS).fill(''));
  };

  return (
    <div className="bl-page">
      <style>{`
        /* Right inset matches the action bars' 18px, so the boxes run out to the same edge as the buttons. */
        .rot-wrap { padding: 22px 18px 28px 28px; max-width: none; flex: 1; }
        /* Section headings: bold, on the same left edge as the captions (owner request 2026-10-14). */
        .rot-sec { font-size: 20px; font-weight: 700; color: var(--ai-ink); margin: 4px 0 18px; }
        .rot-sec--test { margin-top: 19px; }
        /* G5 Round Off Test (owner request 2026-10-14, measured from the G5 screen): 21px XAF control
           boxes on a 4px gap (25px row pitch), 12px regular captions and text, a 140px caption column,
           two blocks side by side with a 10px gap, and the Test boxes in three 10px-gapped columns. */
        .rot-setup { display: grid; grid-template-columns: auto minmax(0,1fr) auto minmax(0,1fr); gap: 4px 0; align-items: center; }
        /* The left-hand field stops 10px short of the right-hand caption column (G5 gutter). Grid items
           keep their stretch only when width is auto, so the margin is taken from the field width. */
        .rot-setup > :nth-child(4n+2) { margin-right: 10px; }
        .rot-setup > .rot-in { width: auto; justify-self: stretch; }
        /* Each caption column is as wide as its longest caption plus 8px, so the boxes run up to their captions. */
        .rot-setup > .rot-label { padding-right: 8px; white-space: nowrap; }
        .rot-label { font-size: 12px; font-weight: 400; color: var(--ai-ink); }
        .rot-in { height: 21px; font-size: 12px; padding: 0 4px; border: 1px solid var(--ai-line); border-radius: 0; color: var(--ai-ink); background: #fff !important; box-sizing: border-box; width: 100%; }
        .rot-in:focus { outline: none; border-color: var(--ai-line); }
        .rot-test { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 4px 10px; }
        .rot-th { font-size: 12px; font-weight: 400; color: var(--ai-ink); line-height: 10px; padding-bottom: 0; }
        /* Display boxes are white in G5 (read-only XAF editors), not grey. */
        .rot-out { height: 21px; font-size: 12px; padding: 0 4px; border: 1px solid var(--ai-line); border-radius: 0; background: #fff; color: var(--ai-ink); box-sizing: border-box; width: 100%; display: flex; align-items: center; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        /* Action bars (top and bottom) use the shared .sd-actionbar / .sd-abtn, as the Method form. */
      `}</style>

      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Test &amp; Method</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Round Off Test</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="set-roundofftest" />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Try the lab rounding rule against sample values — nothing is saved.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div className="bl-card bl-card-flex">
          <div className="sd-actionbar">
            <button type="button" className="sd-abtn primary" onClick={showResults}>Show Results!</button>
            <button type="button" className="sd-abtn" onClick={clearAll}>Clear</button>
          </div>
          <div className="rot-wrap">
            {/* Setup */}
            <div className="rot-sec">Setup</div>
            <div className="rot-setup">
              <SetupField label="Sig Figures:" value={sigFigures} onChange={setSigFigures} />
              <SetupField label="Default Result:" value={defaultResult} onChange={setDefaultResult} />
              <SetupField label="Cut Off:" value={cutOff} onChange={setCutOff} />
              <SetupField label="Rpt Limit:" value={rptLimit} onChange={setRptLimit} />
              <SetupField label="Decimals:" value={decimals} onChange={setDecimals} />
              <SetupField label="Sci Notation Decimals:" value={sciDecimals} onChange={setSciDecimals} />
            </div>

            {/* Test */}
            <div className="rot-sec rot-sec--test">Test</div>
            <div className="rot-test">
              <div className="rot-th">Enter a Numeric Number:</div>
              <div className="rot-th">Display Results:</div>
              <div className="rot-th">Display Scientific Notation:</div>
              {Array.from({ length: ROWS }, (_, i) => (
                <RowCells
                  key={i}
                  value={inputs[i] ?? ''}
                  result={results[i] ?? ''}
                  sci={sci[i] ?? ''}
                  onChange={(v) => setInput(i, v)}
                  onEnter={showResults}
                />
              ))}
            </div>

          </div>
          <div className="sd-actionbar bottom">
            <button type="button" className="sd-abtn primary" onClick={showResults}>Show Results!</button>
            <button type="button" className="sd-abtn" onClick={clearAll}>Clear</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SetupField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <>
      <div className="rot-label">{label}</div>
      <input className="rot-in" value={value} onChange={(e) => onChange(e.target.value)} />
    </>
  );
}

function RowCells({ value, result, sci, onChange, onEnter }: {
  value: string; result: string; sci: string;
  onChange: (v: string) => void; onEnter: () => void;
}) {
  return (
    <>
      <input
        className="rot-in"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
      />
      <div className="rot-out">{result}</div>
      <div className="rot-out">{sci}</div>
    </>
  );
}
